import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { useAcademicsAssessmentsClassesHook } from '../hooks/useAcademicsAssessmentsClassesHook';
import { useAcademicsAssessmentsCourseHook } from '../hooks/useAcademicsAssessmentsCourseHook';
import { useAcademicsFiltersContext } from './academicsFilters.context';
import { useAcademicsAssessmentDetailsHook } from '../hooks/useAcademicsAssessmentsDetailsHook';
import { ClassGradeSubjectStructureProvider } from '../../institute/contexts/classGradeSubjectStructure.context';
import {
  IAcademicsAssessmentClassMapResponse,
  IAcademicsAssessmentCourseMapResponse,
  IAcademicsAssessmentClassMap,
  IAcademicsAssessmentCourseMap,
  EAssessmentType,
  IAcademicsAssessmentDetails,
} from '../../../../apis/academics/academicsAssessments.interfaces';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { UseQueryResult } from '@tanstack/react-query';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';

type TQueryError = IFHError<{ message: string }>;

interface TAcademicsAssessmentsState {
  // Queries
  classesQuery: UseQueryResult<
    IAcademicsAssessmentClassMapResponse,
    TQueryError
  >;
  coursesQuery: UseQueryResult<
    IAcademicsAssessmentCourseMapResponse,
    TQueryError
  >;
  assessmentDetailsQuery: UseQueryResult<
    IAcademicsAssessmentDetails,
    TQueryError
  >;

  // Refetch functions
  refetchClasses: () => void;
  refetchCourses: () => void;

  // Class assessment utility functions
  getClassAssessmentById: (
    id: number
  ) => IAcademicsAssessmentClassMap | undefined;
  getAssessmentsByClass: (
    classId: number
  ) => IAcademicsAssessmentClassMap | undefined;
  getAssessmentsByType: (
    type: EAssessmentType
  ) => IAcademicsAssessmentClassMap[];

  // Course assessment utility functions
  getCourseAssessmentById: (
    id: number
  ) => IAcademicsAssessmentCourseMap | undefined;
  getAssessmentsByCourse: (
    courseId: number
  ) => IAcademicsAssessmentCourseMap | undefined;
  getAssessmentsByCourseAndType: (
    courseId: number,
    type: EAssessmentType
  ) => IAcademicsAssessmentCourseMap | undefined;

  // Assessment details state
  selectedAssessmentId: string | null;
  setSelectedAssessmentId: (id: string | null) => void;
  clearSelectedAssessment: () => void;

  // Loading and error states
  isLoading: boolean;
  isError: boolean;
  error: TQueryError | null;
}

interface AcademicsAssessmentsProviderProps {
  children: ReactNode;
  paginationParams?: TPaginationParams;
}

const AcademicsAssessmentsContext = createContext<
  TAcademicsAssessmentsState | undefined
>(undefined);

export const AcademicsAssessmentsProvider: React.FC<
  AcademicsAssessmentsProviderProps
> = ({ children, paginationParams = {} as TPaginationParams }) => {
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<
    string | null
  >(null);
  const { academicYear, grade } = useAcademicsFiltersContext();
  const assessmentParams = {
    ...paginationParams,
    ...(academicYear && { academicYear }),
    ...(grade && { grade }),
  };
  const classesQuery = useAcademicsAssessmentsClassesHook(assessmentParams);
  const coursesQuery = useAcademicsAssessmentsCourseHook(assessmentParams);
  const assessmentDetailsQuery = useAcademicsAssessmentDetailsHook(
    selectedAssessmentId || '',
    {
      enabled: !!selectedAssessmentId,
    } as Parameters<typeof useAcademicsAssessmentDetailsHook>[1]
  );

  const refetchClasses = useCallback(() => {
    classesQuery.refetch();
  }, [classesQuery]);

  const refetchCourses = useCallback(() => {
    coursesQuery.refetch();
  }, [coursesQuery]);

  // Class assessment utility functions
  const getClassAssessmentById = useCallback(
    (id: number): IAcademicsAssessmentClassMap | undefined => {
      return classesQuery.data?.classAssessments.find((cls) => cls.id === id);
    },
    [classesQuery]
  );

  const getAssessmentsByClass = useCallback(
    (classId: number): IAcademicsAssessmentClassMap | undefined => {
      return classesQuery.data?.classAssessments.find(
        (cls) => cls.id === classId
      );
    },
    [classesQuery]
  );

  const getAssessmentsByType = useCallback(
    (type: EAssessmentType): IAcademicsAssessmentClassMap[] => {
      if (!classesQuery.data) return [];
      return classesQuery.data.classAssessments.filter(
        (classAssessment) =>
          classAssessment.assessments[type] &&
          classAssessment.assessments[type]!.length > 0
      );
    },
    [classesQuery]
  );

  // Course assessment utility functions
  const getCourseAssessmentById = useCallback(
    (id: number): IAcademicsAssessmentCourseMap | undefined => {
      return coursesQuery.data?.courseAssessments.find(
        (course) => course.id === id
      );
    },
    [coursesQuery]
  );

  const getAssessmentsByCourse = useCallback(
    (courseId: number): IAcademicsAssessmentCourseMap | undefined => {
      return coursesQuery.data?.courseAssessments.find(
        (course) => course.id === courseId
      );
    },
    [coursesQuery]
  );

  const getAssessmentsByCourseAndType = useCallback(
    (
      courseId: number,
      type: EAssessmentType
    ): IAcademicsAssessmentCourseMap | undefined => {
      const course = coursesQuery.data?.courseAssessments.find(
        (course) => course.id === courseId
      );
      if (!course || !course.assessments[type]) return undefined;
      return course;
    },
    [coursesQuery]
  );

  const clearSelectedAssessment = useCallback(() => {
    setSelectedAssessmentId(null);
  }, []);

  // Loading and error states
  const isLoading =
    classesQuery.isLoading ||
    coursesQuery.isLoading ||
    assessmentDetailsQuery.isLoading;
  const isError =
    classesQuery.isError ||
    coursesQuery.isError ||
    assessmentDetailsQuery.isError;
  const error =
    classesQuery.error ||
    coursesQuery.error ||
    assessmentDetailsQuery.error ||
    null;

  const value = useMemo(
    () => ({
      // Queries
      classesQuery,
      coursesQuery,
      assessmentDetailsQuery,

      // Refetch functions
      refetchClasses,
      refetchCourses,

      // Class assessment utility functions
      getClassAssessmentById,
      getAssessmentsByClass,
      getAssessmentsByType,

      // Course assessment utility functions
      getCourseAssessmentById,
      getAssessmentsByCourse,
      getAssessmentsByCourseAndType,

      // Assessment details state
      selectedAssessmentId,
      setSelectedAssessmentId,
      clearSelectedAssessment,

      // Loading and error states
      isLoading,
      isError,
      error,
    }),
    [
      getClassAssessmentById,
      getAssessmentsByClass,
      getAssessmentsByType,
      getCourseAssessmentById,
      getAssessmentsByCourse,
      getAssessmentsByCourseAndType,
      isLoading,
      isError,
      error,
      refetchClasses,
      refetchCourses,
      classesQuery,
      coursesQuery,
      assessmentDetailsQuery,
      selectedAssessmentId,
      clearSelectedAssessment,
    ]
  );

  return (
    <ClassGradeSubjectStructureProvider>
      <AcademicsAssessmentsContext.Provider value={value}>
        {children}
      </AcademicsAssessmentsContext.Provider>
    </ClassGradeSubjectStructureProvider>
  );
};

export const useAcademicsAssessmentsContext =
  (): TAcademicsAssessmentsState => {
    const context = useContext(AcademicsAssessmentsContext);

    if (context === undefined) {
      throw new Error(
        'useAcademicsAssessmentsContext must be used within a AcademicsAssessmentsProvider'
      );
    }

    return context;
  };
